import { type Auth0ContextInterface, type User as Auth0User, useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "@xstate/react";

import { type User } from "~/models/user";
import { authenticationActorRef } from "~/state/machines/authentication";

import { isDesktopAppMode } from "./interop/common";

/**
 * Custom hook provider for all auth-related functionality.
 */

/**
 * Context interface for the auth information.
 *
 * This context is very similar to the Auth0Context that that the Auth0 library provides,
 * with some additional overridden properties to meet our implementation. With the
 * exception of `user`, all overridden properties should be functionally identical
 * so that the Auth0 documentation is still usable.
 *
 * Available properties:

 * State booleans (e.g. yes/no):
 * * isAuthenticated
 * * isAuthenticatedOrLocal
 * * isLoading
 * * isLocalApp

 * User data:
 * * user
 * * _Auth0User <-- use this one only if truly necessary

 * Token promises (consider adding anything needed from these as well-named getters):
 * * getAccessTokenSilently
 * * getIdTokenClaims

 * Auth workflow funcs. you shouldn't need these if you aren't implementing auth code.
 * * error
 * * getAccessTokenWithPopup
 * * loginWithRedirect
 * * loginWithPopup
 * * logout
 * * handleRedirectCallback
*/
export interface AuthContext
	extends Omit<
		Auth0ContextInterface,
		"user" | "loginWithRedirect" | "logout" | "getAccessTokenSilently"
	> {
	/**
	 * The Moment definition of a User. If for some reason you need the Auth0
	 * view of a user, it can be found as _Auth0User.
	 */
	user: User | undefined;

	/**
	 * Request an up-to-date, valid access token. This is called "silently"
	 * as in the access token request process itself is completely invisible
	 * to the user (there are visible ways to perform it as well).
	 *
	 * For more information on how this function works, view the documentation
	 * for the auth0-react library.
	 *
	 * @param {boolean} [opts.promptForLogin=false] - use true in order to automatically
	 * launch a login prompt if the user's authentication appears to be out of date.
	 * The mechanics that trigger this can be opaque, and confusing.
	 * A user may still be successfully authenticated unable to receive
	 * a new valid authentication token if their refresh token is invalid.
	 * This flag is therefore kind of aggressive and fundamentally disruptive
	 * to the user, so use it carefully.
	 * @param {string} opts.redirectPath - if promptForLogin is true, also pass
	 * this value to send the user to a specific page after successful login. Typically
	 * this means using `router.asPath` in order to redirect the user back to the current
	 * page. If this is unset, the user will be sent to `/docs` after login.
	 */
	getAccessTokenSilently: (opts?: {
		promptForLogin?: boolean;
		redirectPath?: string;
	}) => Promise<string | undefined>;

	isAuthenticatedOrLocal: boolean;
	isLocalApp: boolean;

	loginWithRedirect: (opts: { redirectPath?: string }) => Promise<void>;
	logoutWithRedirect: (redirectPath?: string) => Promise<void>;

	_Auth0User?: Auth0User;
}

/**
 * Use the Auth0 library to get the auth state and actions.
 *
 * @returns The auth context.
 */
export const useAuth = (): AuthContext => {
	const { user, ...rest } = useAuth0();

	const context = useSelector(authenticationActorRef, (state) => state.context);

	return {
		...rest,
		getAccessTokenSilently: context.getAccessTokenSilently,

		isAuthenticated: context.authnStatus === "authenticated",
		isAuthenticatedOrLocal: context.authnStatus === "authenticated" || isDesktopAppMode(),
		isLocalApp: isDesktopAppMode(),

		loginWithRedirect: context.loginWithRedirect,
		logoutWithRedirect: context.logoutWithRedirect,

		user: context.authnStatus === "authenticated" ? context.user : undefined,
		_Auth0User: user,
	};
};

export const isTokenError = (e) => {
	return (
		e instanceof Error &&
		(("message" in e && e.message === "missing_auth_token") ||
			("error" in e && (e.error === "missing_refresh_token" || e.error === "invalid_grant")))
	);
};
